import { useContext, useEffect, useState } from 'react';
import Loading from '../../../../../commons/Loading/Loading';
import { DashboardResultsContext } from '../DashboardResults.context';
import { useStatsByDay } from '../../../../../../hooks/fetchers/useStatsByDay/useStatsByDay';
import moment from 'moment';
import { formatPrice } from '../../../../../../utils/helpers/price/formatPrice';
import { StatsResultsResponseRow } from '../../../../../../types/api/endpoints/stats/results.types';
import { StatsPeriods } from '../DashboardResults.types';
import { useStatsByWeek } from '../../../../../../hooks/fetchers/useStatsByWeek/useStatsByWeek';
import { useMergedStatesFromFetchers } from '../../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import { useStatsByMonth } from '../../../../../../hooks/fetchers/useStatsByMonth/useStatsByMonth';
import { Stats } from '../../../../../../types/api/endpoints/stats/stats.types';
import { useAllBoosts } from '../../../../../../hooks/fetchers/useAllBoosts/useAllBoosts';
import { useAllBoostsHistory } from '../../../../../../hooks/fetchers/useAllBoostsHistory/useAllBoostsHistory';
import DashboardResultsTableItem from './DashboardResultsTableItem/DashboardResultsTableItem';
import { BaseResponse } from '../../../../../../types/api/api.types';
import { useMutation, useQueryClient } from 'react-query';
import { AddBoostQueryParams } from './DashboardResultsTable.types';
import {
  ADD_BOOST_QUERY_KEY,
  REMOVE_BOOST_QUERY_KEY,
  addBoostQuery,
  removeBoostQuery,
} from './DashboardResultsTable.query';

const calculateStatsFieldTotal = (stats: Stats[], field: keyof Stats) => {
  return stats.reduce((acc, stat) => {
    const currentField = stat[field];
    if (typeof currentField === 'number') {
      return acc + ((stat[field] as number) || 0);
    } else {
      return acc;
    }
  }, 0);
};

export default () => {
  const queryClient = useQueryClient();

  const { currentPeriod, currentDates } = useContext(DashboardResultsContext);

  const [from, to] = currentDates;

  const [stats, setStats] = useState<StatsResultsResponseRow[]>();

  const [order, setOrder] = useState<keyof Stats>('created_at');

  const {
    data: dayStats,
    refetch: refetchDay,
    isLoading: isDayLoading,
  } = useStatsByDay({ 
    day: moment(from).format('YYYY-MM-DD'),
    order
  }, { enabled: false });

  const {
    data: weekStats,
    refetch: refetchWeek,
    isLoading: isWeekLoading,
  } = useStatsByWeek(
    {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(to).format('YYYY-MM-DD'),
      order
    },
    { enabled: false }
  );

  const {
    data: monthStats,
    refetch: refetchMonth,
    isLoading: isMonthLoading,
  } = useStatsByMonth(
    {
      date: moment(from).format('YYYY-MM-DD'),
      order
    },
    { enabled: false }
  );

  const { data: allBoosts, isLoading: isAllBoostsLoading } = useAllBoosts({
    refetchOnMount: false
  });

  const { 
    data: allBoostsHistory, 
    isLoading: isAllBoostsHistoryLoading,
    // refetch: refetchAllBoostsHistory
  } = useAllBoostsHistory({ 
    page: 1, 
    perPage: 10000, 
    period: currentPeriod 
  });

  const { mutate: addBoost, isLoading: isAddBoostLoading } = useMutation<
    Awaited<BaseResponse>,
    Error,
    AddBoostQueryParams
  >((data) => addBoostQuery(data), {
    mutationKey: ADD_BOOST_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: ({ message }) => {
      alert(message);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const { mutate: removeBoost, isLoading: isRemoveBoostLoading } = useMutation<
    Awaited<BaseResponse>,
    Error,
    number
  >((data) => removeBoostQuery(data), {
    mutationKey: REMOVE_BOOST_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: ({ message }) => {
      alert(message);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  useEffect(() => {
    if (currentPeriod === StatsPeriods.Day) {
      setStats(dayStats);
    }
  }, [currentPeriod, dayStats]);

  useEffect(() => {
    if (currentPeriod === StatsPeriods.Week || currentPeriod === StatsPeriods.Custom) {
      setStats(weekStats);
    }
  }, [currentPeriod, weekStats]);

  useEffect(() => {
    if (currentPeriod === StatsPeriods.Month) {
      setStats(monthStats);
    }
  }, [currentPeriod, monthStats]);

  useEffect(() => {
    switch (currentPeriod) {
      case StatsPeriods.Day:
        refetchDay();
        break;
      case StatsPeriods.Week:
        refetchWeek();
        break;
      case StatsPeriods.Month:
        refetchMonth();
        break;
      case StatsPeriods.Custom:
        refetchWeek();
        break;
    }
  }, [currentPeriod, currentDates, order]);

  const { isLoading } = useMergedStatesFromFetchers({
    loadings: [
      isDayLoading,
      isWeekLoading,
      isMonthLoading,
      isAllBoostsLoading,
      isAllBoostsHistoryLoading,
      isAddBoostLoading,
      isRemoveBoostLoading,
    ],
  });

  return (
    <table
      className="dashboard__table dashboard__result-table dashboard-commerciaux__table"
      style={{ flex: 0 }}>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <thead>
            <tr>
              <th onClick={() => setOrder('id_commercial')}>Commercial</th>
              <th onClick={() => setOrder('won_prospects_total')}>Gagnés</th>
              <th onClick={() => setOrder('lost_prospects_total')}>Perdus</th>
              <th onClick={() => setOrder('to_be_remind_prospects_total')}>À rappeler</th>
              <th onClick={() => setOrder('prospects_total')}>Prospectés</th>
              <th onClick={() => setOrder('win_prospects_ratio')}>Ratio</th>
              <th onClick={() => setOrder('win_prospects_average')}>Moyenne</th>
              <th onClick={() => setOrder('commissions_sum')}>Commissions</th>
              <th onClick={() => setOrder('sales_revenue')}>C.A</th>
            </tr>
          </thead>
          <tbody>
            {stats?.map(
              (
                {
                  id,
                  firstName,
                  lastName,
                  won_prospects_total,
                  lost_prospects_total,
                  to_be_remind_prospects_total,
                  prospects_total,
                  win_prospects_ratio,
                  win_prospects_average,
                  sales_revenue,
                  commissions_sum,
                },
                index
              ) => (
                <tr key={index}>
                  <td>
                    {firstName} {lastName}
                  </td>
                  <DashboardResultsTableItem
                    isHigher={
                      won_prospects_total ===
                        Math.max(...stats.map((item) => item.won_prospects_total)) &&
                      won_prospects_total > 0
                    }
                    isActive={Boolean(
                      allBoostsHistory?.results.find(
                        (history) =>
                          history.id_stat === id &&
                          history.period === currentPeriod &&
                          history.stats_key === 'won_prospects_total'
                      )
                    )}
                    {...(allBoosts?.find(
                      (boost) => boost.stats_key === 'won_prospects_total' && boost.is_active
                    ) &&
                      currentPeriod !== StatsPeriods.Custom && {
                        onClick: () => {
                          const boostHistoryId = allBoostsHistory?.results.find(
                            (history) =>
                              history.id_stat === id &&
                              history.period === currentPeriod &&
                              history.stats_key === 'won_prospects_total'
                          )?.id;
                          if (boostHistoryId) {
                            removeBoost(boostHistoryId);
                          } else {
                            const confirmAddBoost = confirm('Êtes-vous sûr de vouloir ajouter ce boost ?')
                            if (confirmAddBoost) {
                              addBoost({
                                id_stat: id,
                                stats_key: 'won_prospects_total',
                                period: currentPeriod,
                              });
                            }
                          }
                        },
                      })}>
                    {won_prospects_total}
                  </DashboardResultsTableItem>
                  <DashboardResultsTableItem>{lost_prospects_total}</DashboardResultsTableItem>
                  <DashboardResultsTableItem>
                    {to_be_remind_prospects_total}
                  </DashboardResultsTableItem>
                  <DashboardResultsTableItem
                    isHigher={
                      prospects_total === Math.max(...stats.map((item) => item.prospects_total)) &&
                      prospects_total > 0
                    }
                    isActive={Boolean(
                      allBoostsHistory?.results.find(
                        (history) =>
                          history.id_stat === id &&
                          history.period === currentPeriod &&
                          history.stats_key === 'prospects_total'
                      )
                    )}
                    {...(allBoosts?.find(
                      (boost) => {
                        console.log('boost.stats_key === prospects_total && boost.is_active', boost.stats_key === 'prospects_total' && boost.is_active)
                        return boost.stats_key === 'prospects_total' && boost.is_active
                      }
                    ) &&
                      currentPeriod !== StatsPeriods.Custom && {
                        onClick: () => {
                          console.log('ahhhh')
                          const boostHistoryId = allBoostsHistory?.results.find(
                            (history) =>
                              history.id_stat === id &&
                              history.period === currentPeriod &&
                              history.stats_key === 'prospects_total'
                          )?.id;
                          if (boostHistoryId) {
                            removeBoost(boostHistoryId);
                          } else {
                            const confirmAddBoost = confirm('Êtes-vous sûr de vouloir ajouter ce boost ?')
                            if (confirmAddBoost) {
                              addBoost({
                                id_stat: id,
                                stats_key: 'prospects_total',
                                period: currentPeriod,
                              });
                            }
                          }
                        },
                      })}>
                    {prospects_total}
                  </DashboardResultsTableItem>
                  <DashboardResultsTableItem
                    isHigher={
                      win_prospects_ratio ===
                        Math.max(...stats.map((item) => item.win_prospects_ratio)) &&
                      win_prospects_ratio > 0
                    }
                    isActive={Boolean(
                      allBoostsHistory?.results.find(
                        (history) =>
                          history.id_stat === id &&
                          history.period === currentPeriod &&
                          history.stats_key === 'win_prospects_ratio'
                      )
                    )}
                    {...(allBoosts?.find(
                      (boost) => boost.stats_key === 'win_prospects_ratio' && boost.is_active
                    ) &&
                      currentPeriod !== StatsPeriods.Custom && {
                        onClick: () => {
                          const boostHistoryId = allBoostsHistory?.results.find(
                            (history) =>
                              history.id_stat === id &&
                              history.period === currentPeriod &&
                              history.stats_key === 'win_prospects_ratio'
                          )?.id;
                          if (boostHistoryId) {
                            removeBoost(boostHistoryId);
                          } else {
                            const confirmAddBoost = confirm('Êtes-vous sûr de vouloir ajouter ce boost ?')
                            if (confirmAddBoost) {
                              addBoost({
                                id_stat: id,
                                stats_key: 'win_prospects_ratio',
                                period: currentPeriod,
                              });
                            }
                          }
                        },
                      })}>
                    {win_prospects_ratio.toFixed(2)}
                  </DashboardResultsTableItem>
                  <DashboardResultsTableItem
                    isHigher={
                      win_prospects_average ===
                        Math.max(...stats.map((item) => item.win_prospects_average)) &&
                      win_prospects_average > 0
                    }
                    isActive={Boolean(
                      allBoostsHistory?.results.find(
                        (history) =>
                          history.id_stat === id &&
                          history.period === currentPeriod &&
                          history.stats_key === 'win_prospects_average'
                      )
                    )}
                    {...(allBoosts?.find(
                      (boost) => boost.stats_key === 'win_prospects_average' && boost.is_active
                    ) &&
                      currentPeriod !== StatsPeriods.Custom && {
                        onClick: () => {
                          const boostHistoryId = allBoostsHistory?.results.find(
                            (history) =>
                              history.id_stat === id &&
                              history.period === currentPeriod &&
                              history.stats_key === 'win_prospects_average'
                          )?.id;
                          if (boostHistoryId) {
                            removeBoost(boostHistoryId);
                          } else {
                            const confirmAddBoost = confirm('Êtes-vous sûr de vouloir ajouter ce boost ?')
                            if (confirmAddBoost) {
                              addBoost({
                                id_stat: id,
                                stats_key: 'win_prospects_average',
                                period: currentPeriod,
                              });
                            }
                          }
                        },
                      })}>
                    {win_prospects_average.toFixed(2)}
                  </DashboardResultsTableItem>
                  <DashboardResultsTableItem
                    isHigher={
                      commissions_sum === Math.max(...stats.map((item) => item.commissions_sum)) &&
                      commissions_sum > 0
                    }
                    isActive={Boolean(
                      allBoostsHistory?.results.find(
                        (history) =>
                          history.id_stat === id &&
                          history.period === currentPeriod &&
                          history.stats_key === 'commissions_sum'
                      )
                    )}
                    {...(allBoosts?.find(
                      (boost) => boost.stats_key === 'commissions_sum' && boost.is_active
                    ) &&
                      currentPeriod !== StatsPeriods.Custom && {
                        onClick: () => {
                          const boostHistoryId = allBoostsHistory?.results.find(
                            (history) =>
                              history.id_stat === id &&
                              history.period === currentPeriod &&
                              history.stats_key === 'commissions_sum'
                          )?.id;
                          if (boostHistoryId) {
                            removeBoost(boostHistoryId);
                          } else {
                            const confirmAddBoost = confirm('Êtes-vous sûr de vouloir ajouter ce boost ?')
                            if (confirmAddBoost) {
                              addBoost({
                                id_stat: id,
                                stats_key: 'commissions_sum',
                                period: currentPeriod,
                              });
                            }
                          }
                        },
                      })}>
                    {`${formatPrice(commissions_sum)}€`}
                  </DashboardResultsTableItem>
                  <DashboardResultsTableItem
                    isHigher={
                      sales_revenue === Math.max(...stats.map((item) => item.sales_revenue)) &&
                      sales_revenue > 0
                    }
                    isActive={Boolean(
                      allBoostsHistory?.results.find(
                        (history) =>
                          history.id_stat === id &&
                          history.period === currentPeriod &&
                          history.stats_key === 'sales_revenue'
                      )
                    )}
                    {...(allBoosts?.find(
                      (boost) => boost.stats_key === 'sales_revenue' && boost.is_active
                    ) &&
                      currentPeriod !== StatsPeriods.Custom && {
                        onClick: () => {
                          const boostHistoryId = allBoostsHistory?.results.find(
                            (history) =>
                              history.id_stat === id &&
                              history.period === currentPeriod &&
                              history.stats_key === 'sales_revenue'
                          )?.id;
                          if (boostHistoryId) {
                            removeBoost(boostHistoryId);
                          } else {
                            const confirmAddBoost = confirm('Êtes-vous sûr de vouloir ajouter ce boost ?')
                            if (confirmAddBoost) {
                              addBoost({
                                id_stat: id,
                                stats_key: 'sales_revenue',
                                period: currentPeriod,
                              });
                            }
                          }
                        },
                      })}>
                    {`${formatPrice(sales_revenue)}€`}
                  </DashboardResultsTableItem>
                </tr>
              )
            )}
            {stats && (
              <tr className="total_row">
                <th>Total</th>
                <th>{calculateStatsFieldTotal(stats, 'won_prospects_total')}</th>
                <th>{calculateStatsFieldTotal(stats, 'lost_prospects_total')}</th>
                <th>{calculateStatsFieldTotal(stats, 'to_be_remind_prospects_total')}</th>
                <th>{calculateStatsFieldTotal(stats, 'prospects_total')}</th>
                <th>
                  {(
                    calculateStatsFieldTotal(stats, 'win_prospects_ratio') / stats.length || 0
                  ).toFixed(2)}
                </th>
                <th>
                  {(
                    calculateStatsFieldTotal(stats, 'win_prospects_average') / stats.length || 0
                  ).toFixed(2)}
                </th>
                <th>{formatPrice(calculateStatsFieldTotal(stats, 'commissions_sum'))}€</th>
                <th>{formatPrice(calculateStatsFieldTotal(stats, 'sales_revenue'))}€</th>
              </tr>
            )}
          </tbody>
        </>
      )}
    </table>
  );
};
